import { useMemo, useState } from 'react';
import { requireSatellite } from './config';
import { SatelliteLayout } from './layout';
import { trackSatelliteAction } from './analytics';
import {
  ROTATION_PRESETS,
  requireRotation,
  addDays,
  addMonths,
  daysInMonth,
  formatYmd,
  monthLabel,
  parentOnDate,
  parseYmd,
  startOfMonth,
  todayYmd,
  weekdayMonday0,
  type ParentId,
  type Ymd,
} from './dates';
import { buildParentCalendar, downloadIcs } from './ics';
import { MonthGrid } from './MonthGrid';

const CONFIG = requireSatellite('custodyscheduleapp.com');

export function CustodySchedulePage() {
  const [preset, setPreset] = useState('2-2-3');
  const [parentA, setParentA] = useState('Parent A');
  const [parentB, setParentB] = useState('Parent B');
  const [startInput, setStartInput] = useState(formatYmd(todayYmd()));
  const cycle = requireRotation(preset).cycle;
  const start = parseYmd(startInput) ?? todayYmd();
  // The every-weekend preset is defined by real weekdays, not by an
  // arbitrary cycle start: A has Mon–Thu and B has Fri–Sun.
  const rotationAnchor =
    preset === 'every-weekend' ? addDays(start, -weekdayMonday0(start)) : start;
  const names = { A: parentA.trim() || 'Parent A', B: parentB.trim() || 'Parent B' };

  const months = useMemo(() => {
    const first = startOfMonth(start);
    return [first, addMonths(first, 1)];
  }, [start]);

  function exportCalendar() {
    const days = [];
    for (let i = 0; i < 365; i += 1) {
      const date = addDays(start, i);
      days.push({ date, parent: parentOnDate(date, rotationAnchor, cycle) });
    }
    downloadIcs(
      `${preset}.ics`,
      buildParentCalendar(days, names, requireRotation(preset).label),
    );
    trackSatelliteAction('Satellite Calendar Exported', CONFIG, { rotation: preset });
  }

  return (
    <SatelliteLayout config={CONFIG}>
      <form className='grid gap-4 sm:grid-cols-2 print:hidden'>
        <label className='text-sm sm:col-span-2'>
          <span className='mb-1 block font-medium'>Rotation</span>
          <select
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={preset}
            onChange={(event) => setPreset(event.target.value)}
          >
            {Object.entries(ROTATION_PRESETS).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Parent A</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={parentA}
            onChange={(event) => setParentA(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Parent B</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={parentB}
            onChange={(event) => setParentB(event.target.value)}
          />
        </label>
        <label className='text-sm sm:col-span-2'>
          <span className='mb-1 block font-medium'>Preview and export from</span>
          <input
            type='date'
            className='w-full rounded-lg border border-gray-300 px-3 py-2 sm:w-56'
            value={startInput}
            onChange={(event) => setStartInput(event.target.value)}
          />
        </label>
      </form>

      <div className='mt-6 space-y-8'>
        {months.map((month) => (
          <MonthCalendar
            key={`${month.year}-${month.month}`}
            month={month}
            anchor={rotationAnchor}
            cycle={cycle}
            names={names}
          />
        ))}
      </div>

      <div className='mt-6 flex flex-wrap gap-3 print:hidden'>
        <button
          type='button'
          className='rounded-xl bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-hover'
          onClick={exportCalendar}
        >
          Download .ics
        </button>
        <button
          type='button'
          className='rounded-xl border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-50'
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>
    </SatelliteLayout>
  );
}

function MonthCalendar({
  month,
  anchor,
  cycle,
  names,
}: {
  month: Ymd;
  anchor: Ymd;
  cycle: ParentId[];
  names: { A: string; B: string };
}) {
  const days: { date: Ymd; parent: ParentId }[] = [];
  const count = daysInMonth(month.year, month.month);
  for (let day = 1; day <= count; day += 1) {
    const date = { year: month.year, month: month.month, day };
    days.push({ date, parent: parentOnDate(date, anchor, cycle) });
  }
  return (
    <section>
      <h2 className='mb-3 text-lg font-semibold'>{monthLabel(month)}</h2>
      <MonthGrid days={days} padStart={weekdayMonday0(month)} names={names} />
    </section>
  );
}
